import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FullscreenModal, Button, CalendarInput, DropFileInput } from "shared";
import Input from "shared/Input/Input";
import {
  useCloseAsset,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import "./CloseRealEstateModal.scss";

interface CloseRealEstateModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

interface FormValues {
  salePrice: string;
  closedAt: string;
  contractFile: File | null;
}

const validationSchema = Yup.object({
  salePrice: Yup.number()
    .required("Cena sprzedaży jest wymagana")
    .positive("Cena musi być dodatnia"),
  closedAt: Yup.string().required("Data sprzedaży jest wymagana"),
  contractFile: Yup.mixed().nullable(),
});

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: "Mieszkanie",
  house: "Dom",
  land: "Działka",
};

const getPropertyLabel = (item: any): string => {
  if (!item) return "";
  const propertyType = item.details?.propertyType;
  if (!propertyType) return item.name || "";
  if (propertyType === "other")
    return item.details?.propertyTypeOther || "Inne";
  return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
};

const CloseRealEstateModal = ({
  isOpen,
  onClose,
  item,
}: CloseRealEstateModalProps) => {
  const { mutateAsync: closeAsset, isPending } = useCloseAsset();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const initialValues: FormValues = {
    salePrice: "",
    closedAt: "",
    contractFile: null,
  };

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      await closeAsset(
        {
          uuid: item.uuid,
          closedAt: values.closedAt,
          salePrice: Number(values.salePrice),
          currencyUuid: item.currency?.uuid,
        },
        {
          onSuccess: async () => {
            if (values.contractFile instanceof File) {
              try {
                const userId: string | number =
                  (me as any)?.user?.id ?? (me as any)?.id ?? "";

                const uploaded = await uploadFile({
                  file: values.contractFile,
                  type: "attachment",
                  parentId: userId,
                });

                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;

                if (fileUuid && item.uuid) {
                  await attachFile({
                    investmentUuid: item.uuid,
                    fileUuid,
                    role: "closure_contract",
                  });
                }
              } catch {
                // Plik nie blokuje zamknięcia aktywa
              }
            }

            onClose();
          },
        },
      );
    } catch {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  const propertyLabel = getPropertyLabel(item);
  const currencySymbol = item.currency?.currency || "";

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-real-estate-modal">
        <h2 className="fullscreen-modal__header">
          Sprzedaż Nieruchomości &quot;{propertyLabel}&quot;
        </h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-real-estate-modal__form">
              <div className="close-real-estate-modal__fields">
                <div className="close-real-estate-modal__field">
                  <Input
                    type="number"
                    name="salePrice"
                    label="Cena sprzedaży"
                    precision={2}
                    data-suffix={currencySymbol}
                    error={
                      (formik.touched.salePrice || formik.submitCount > 0) &&
                      formik.errors.salePrice
                        ? String(formik.errors.salePrice)
                        : undefined
                    }
                  />
                </div>

                <div className="close-real-estate-modal__field">
                  <Field
                    type="date"
                    name="closedAt"
                    label="Data sprzedaży"
                    component={CalendarInput}
                    errors={formik.errors}
                  />
                </div>

                <div className="close-real-estate-modal__field">
                  <Field
                    label="Umowa"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                  />
                </div>
              </div>

              <div className="close-real-estate-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseRealEstateModal;
